/**
 * Synchronisation Immoweb → biens (outil C-Level).
 *
 * Lecture des tables `immoweb_sync_runs` / `immoweb_sync_items` (RLS : rôle
 * c_level seul) et appels à l'Edge Function `immoweb-sync`. Le moteur de
 * conversion / comparaison vit dans `supabase/functions/_shared/immoweb-classified.ts`.
 */
import { supabase } from "@/lib/supabase";
import {
  takes,
  type SyncDecision,
} from "../../supabase/functions/_shared/immoweb-classified";

export { takes, type SyncDecision };

export type SyncRunStatus = "listing" | "analysing" | "ready" | "applying" | "done" | "error";

/**
 * Trois issues par annonce (règle Thibaud 2026-10-19 : Immoweb fait foi) +
 * les biens « Disponible » qui ne sont plus en ligne.
 */
export type SyncCategory = "update" | "create" | "review" | "vanished";

/** Ordre d'affichage des onglets. */
export const SYNC_CATEGORIES: SyncCategory[] = ["update", "create", "review", "vanished"];

export interface SyncRun {
  id: string;
  created_by: string | null;
  status: SyncRunStatus;
  stats: {
    location?: { announced: number; read: number };
    vente?: { announced: number; read: number };
    counts?: Partial<Record<SyncCategory | "error" | "pending", number>>;
  };
  error: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface SyncDiffEntry {
  field: string;
  ours: unknown;
  theirs: unknown;
  kind: "fill" | "conflict";
}

export interface SyncCandidate {
  id: string;
  referenceInterne: string | null;
  adresse: string | null;
  transaction: string;
  disponibilite: string;
  reason: string;
}

export interface SyncItem {
  id: string;
  run_id: string;
  code_immoweb: string | null;
  transaction: "location" | "vente";
  external_ref: string | null;
  prefix: string | null;
  title_fr: string | null;
  address: string | null;
  status: "pending" | "analysed" | "error";
  category: SyncCategory | null;
  match_method: "code" | "external_ref" | "reference_interne" | "other_transaction" | "manual" | null;
  bien_id: string | null;
  candidates: SyncCandidate[];
  scraped: Record<string, unknown> | null;
  diff: SyncDiffEntry[];
  unknown_fields: { path: string; value: unknown; reason: string }[];
  photos: string[];
  decision: SyncDecision | null;
  applied_at: string | null;
  apply_result: {
    bien_id?: string;
    created?: boolean;
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    photos?: number;
    photoError?: string;
    reverted_at?: string;
    error?: string;
    skipped?: boolean;
  } | null;
  error: string | null;
  updated_at: string;
}

export interface BienSummary {
  id: string;
  referenceInterne: string | null;
  adresse: string | null;
  transaction: string;
  disponibilite: string;
}

/** Lien public vers l'annonce (slugs génériques : Immoweb redirige vers l'URL canonique). */
export function immowebUrl(code: string, transaction: "location" | "vente"): string {
  return `https://www.immoweb.be/fr/annonce/x/${transaction === "vente" ? "a-vendre" : "a-louer"}/x/0000/${code}`;
}

export async function fetchLatestSyncRun(): Promise<SyncRun | null> {
  const { data, error } = await supabase
    .from("immoweb_sync_runs")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as SyncRun | null) ?? null;
}

export async function fetchSyncItems(runId: string): Promise<SyncItem[]> {
  const { data, error } = await supabase
    .from("immoweb_sync_items")
    .select("*")
    .eq("run_id", runId)
    .order("transaction")
    .order("prefix", { nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data as SyncItem[]) ?? [];
}

/**
 * Appel de l'Edge Function. `functions.invoke` ne renvoie qu'un message
 * générique (« non-2xx status code ») : on lit le corps JSON de la réponse
 * pour afficher le vrai motif (rôle refusé, analyse déjà en cours…).
 */
async function invokeSync<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>("immoweb-sync", { body });
  if (error) {
    let message = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const payload = await ctx.json();
        if (payload?.error) message = String(payload.error);
      } catch {
        /* corps non JSON : on garde le message générique */
      }
    }
    throw new Error(message);
  }
  if (!data) throw new Error("Réponse vide de immoweb-sync");
  return data;
}

export const startSyncRun = () =>
  invokeSync<{ run_id: string; total: number; stats: SyncRun["stats"] }>({ action: "start" });

export const processSyncRun = (runId: string) =>
  invokeSync<{ run_id: string; done: boolean; remaining: number; counts?: SyncRun["stats"]["counts"] }>({
    action: "process",
    run_id: runId,
  });

export const associateSyncItem = (itemId: string, bienId: string | null) =>
  invokeSync<{ ok: boolean; category: SyncCategory }>({ action: "associate", item_id: itemId, bien_id: bienId });

export const unassociateSyncItem = (itemId: string) =>
  invokeSync<{ ok: boolean }>({ action: "unassociate", item_id: itemId });

export const decideSyncItem = (itemId: string, decision: SyncDecision) =>
  invokeSync<{ ok: boolean }>({ action: "decide", item_id: itemId, decision });

export const searchBiens = (q: string) =>
  invokeSync<{ results: BienSummary[] }>({ action: "search", q }).then((r) => r.results);

export const revertSyncItem = (itemId: string) =>
  invokeSync<{ ok: boolean }>({ action: "revert", item_id: itemId });

export const applySyncItem = (itemId: string) =>
  invokeSync<{ ok: boolean; skipped?: boolean; already?: boolean; result?: SyncItem["apply_result"] }>({
    action: "apply",
    item_id: itemId,
  });

/**
 * Références des biens rattachés (affichées sur les cartes). Lues via l'Edge
 * Function : la RLS de `biens` ne montre au C-Level que les biens « Publié ».
 */
export async function fetchBienSummaries(ids: string[]): Promise<Record<string, BienSummary>> {
  if (!ids.length) return {};
  const { results } = await invokeSync<{ results: BienSummary[] }>({ action: "biens", ids });
  return Object.fromEntries(results.map((b) => [b.id, b]));
}

/** Catégories où l'application écrit quelque chose. */
export const APPLICABLE: SyncCategory[] = ["update", "create", "vanished"];

/**
 * Ce que l'application fera pour une annonce, selon ses choix (sert au résumé
 * en bas de carte et au récapitulatif avant application).
 */
export function planFor(item: SyncItem) {
  const d = item.decision ?? {};
  const plan = { skip: !!d.skip, fills: 0, overwrites: 0, photos: false, create: false, merge: false, status: null as string | null };
  if (plan.skip || item.applied_at || !item.category || !APPLICABLE.includes(item.category)) return plan;
  if (item.category === "vanished") {
    plan.status = d.vanished === "LOUÉ" || d.vanished === "VENDU" ? d.vanished : null;
    return plan;
  }
  if (item.category === "create") {
    plan.create = true;
    plan.photos = item.photos.length > 0;
    return plan;
  }
  for (const e of item.diff) {
    if (!takes(d, e, item.category)) continue;
    if (e.field === "photos") plan.photos = true;
    else if (e.kind === "fill") plan.fills++;
    else plan.overwrites++;
  }
  return plan;
}

/**
 * Annonce dont le seul changement est « photos » avec le même nombre de part et
 * d'autre : c'est la même annonce, déjà importée (règle Thibaud 2026-10-20).
 */
export function isPhotosOnlySame(item: SyncItem): boolean {
  if (item.category !== "update" || item.diff.length !== 1) return false;
  const d = item.diff[0];
  return d.field === "photos" && typeof d.ours === "string" && d.ours === d.theirs;
}

/** Vrai si l'application de cette annonce écrirait quelque chose. */
export function hasWork(item: SyncItem): boolean {
  const p = planFor(item);
  return !p.skip && (p.create || p.photos || p.fills > 0 || p.overwrites > 0 || !!p.status);
}

/** Compteurs par catégorie calculés côté client (à jour pendant l'analyse). */
export function countByCategory(items: SyncItem[]): Record<SyncCategory | "error" | "pending", number> {
  const counts = Object.fromEntries(
    [...SYNC_CATEGORIES, "error", "pending"].map((k) => [k, 0]),
  ) as Record<SyncCategory | "error" | "pending", number>;
  for (const i of items) {
    if (i.status === "pending") counts.pending++;
    else if (i.status === "error") counts.error++;
    else if (i.category) counts[i.category]++;
  }
  return counts;
}

